<template>
  <b-sidebar
    id="sidebar-watchlist"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetAll"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("omnicloud_fr.watch_list.title", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- Name -->
          <validation-provider #default="validationContext" name="name" rules="required">
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.master_account.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Comment -->
          <validation-provider
            #default="validationContext"
            name="comment"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.watch_list.comment')"
              label-for="comment"
            >
              <b-form-input
                id="comment"
                v-model="itemData.comment"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Color -->
          <validation-provider #default="validationContext" name="color" rules="required">
            <b-form-group
              class="text-capitalize"
              :label="$t('alarm_level.color')"
              label-for="color"
            >
              <b-form-input
                id="color"
                v-model="formattedColor"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Type Alarm -->
          <validation-provider
            #default="validationContext"
            :name="$t('omnicloud_fr.watch_list.type_alarm')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.watch_list.type_alarm')"
              label-for="alarm_type"
            >
              <v-select
                v-model="itemData.alarm_type"
                :options="typeAlarmsOptions"
                :clearable="false"
                :reduce="(option) => option.id"
                input-id="alarm_type"
                label="name"
                required
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Tenant -->
          <validation-provider
            #default="validationContext"
            name="tenant"
            rules="required"
          >
            <b-form-group class="text-capitalize" label="Tenant" label-for="tenant">
              <v-select
                v-model="itemData.id_tenant"
                :options="tenantOptions"
                :clearable="false"
                :reduce="(option) => option.value"
                input-id="tenant"
                label="label"
                required
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Is Blacklist -->
          <b-form-group
            class="text-capitalize"
            :label="$t('omnicloud_fr.is_blacklist')"
            label-for="is_blacklist"
          >
            <b-form-checkbox
              v-model="itemData.is_blacklist"
              :value="true"
              :unchecked-value="false"
              switch
            />
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    typeAlarmsOptions: {
      type: Array,
      required: true,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { refFormObserver, getValidationState, resetForm } = formValidation();
    const { t } = useI18nUtils();
    const toast = useToast();

    const blankData = {
      id: null,
      name: "",
      comment: "",
      color: "000000",
      alarm_type: null,
      id_tenant: null,
      is_blacklist: false,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const formattedColor = computed({
      get: () => (itemData.value.color ? `#${itemData.value.color}` : "#000000"),
      set: (val) => {
        itemData.value.color = val.replace("#", "");
      },
    });

    const translations = {
      "omnicloud_fr.watch_list.successful_creation": t(
        "omnicloud_fr.watch_list.successful_creation"
      ),
      "omnicloud_fr.watch_list.error_creation": t(
        "omnicloud_fr.watch_list.error_creation"
      ),
      "omnicloud_fr.watch_list.successful_edit": t(
        "omnicloud_fr.watch_list.successful_edit"
      ),
      "omnicloud_fr.watch_list.error_edit": t("omnicloud_fr.watch_list.error_edit"),
    };

    const loadData = () => {
      if (props.isAdd) {
        itemData.value = JSON.parse(JSON.stringify(blankData));
      } else {
        itemData.value = {
          ...JSON.parse(JSON.stringify(blankData)),
          ...props.itemEdit,
        };
      }
    };

    const resetAll = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      if (refFormObserver.value) {
        refFormObserver.value.reset();
      }
    };

    const onSubmit = async () => {
      try {
        let response;

        if (props.isAdd) {
          response = await store.dispatch("omnicloud_watch_list/add", itemData.value);
          if (response.id) {
            toast({
              component: ToastificationContent,
              props: {
                title:
                  response.message ||
                  translations["omnicloud_fr.watch_list.successful_creation"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: translations["omnicloud_fr.watch_list.error_creation"],
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        } else {
          const idWatchList = itemData.value.id;
          response = await store.dispatch("omnicloud_watch_list/edit", {
            id: idWatchList,
            params: itemData.value,
          });
          if (response.id) {
            toast({
              component: ToastificationContent,
              props: {
                title:
                  response.message ||
                  translations["omnicloud_fr.watch_list.successful_edit"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: translations["omnicloud_fr.watch_list.error_edit"],
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        }
      } catch (error) {
        console.log("error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: props.isAdd
              ? translations["omnicloud_fr.watch_list.error_creation"]
              : translations["omnicloud_fr.watch_list.error_edit"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    return {
      refFormObserver,
      getValidationState,
      itemData,
      formattedColor,
      loadData,
      resetAll,
      onSubmit,
      required,
    };
  },
};
</script>
